import { LitElement, html, property, query } from 'lit-element';

import { FireMixin } from '@forter/mixins/fire-mixin';

import style from './fc-sort-control.css';

const ASC = 'asc';
const DESC = 'desc';
const NONE = 'none';

export const SORT = { ASC, DESC, NONE };

/**
 * Provides a binary "Ascending/Descending"  control for sortable tables.
 * @element fc-sort-control
 * @fires sort
 */
export class FcSortControl extends FireMixin(LitElement) {
  /**
   * The current sort direction
   * @type {'asc'|'desc'|'none'}
   * @attr
   */
  @property({ type: String, reflect: true }) direction;

  /**
   * The sort level that will be displayed in a label
   * @type {Number}
   * @attr
   */
  @property({ type: String }) level;

  /**
   * Whether or not multiple column sort is enabled. example: `true`
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean, attribute: 'prevent-secondary-sort' }) preventSecondarySort = false;

  /**
   * The current direction of the buttons
   * @return {'asc'|'desc'|'none'}
   */
  get selectedDirection() {
    return this.checked && this.checked.value;
  }

  /**
   * The ascending control
   * @type {HTMLInputElement}
   */
  @query('#asc') asc;

  /**
   * The descending control
   * @type {HTMLInputElement}
   */
  @query('#desc') desc;

  /**
   * The control for the selected direction
   * @type {HTMLInputElement}
   */
  @query(':checked') checked;

  /** @inheritdoc */
  static is = 'fc-sort-control';

  /** @inheritdoc */
  static styles = [
    style,
  ];

  firstUpdated() {
    const initialDirection = this.direction;
    if (initialDirection) {
      this[initialDirection].checked = true;
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'direction') {
      if (this[newValue]) {
        this[newValue].checked = true;
      } else {
        if (this.asc) {
          this.asc.checked = false;
        }
        if (this.desc) {
          this.desc.checked = false;
        }
      }
    }
  }

  /** @inheritdoc */
  render() {
    return html`
      <form @change="${this.onChange}">
        <input id="asc" type="radio" name="direction" value="asc"/>
        <label @click="${this.onClick}" direction="asc" aria-label="Ascending"></label>
        <input id="desc" type="radio" name="direction" value="desc"/>
        <label @click="${this.onClick}" direction="desc" aria-label="Descending"></label>
      </form>
      <span>${this.level}</span>
    `;
  }

  /**
   * Fires a 'sort' event whenever the selected direction changes
   * @fires sort
   */
  onChange() {
    this.direction = this.selectedDirection;
    this.fire('sort', { detail: this.direction });
  }

  onClick({ target }) {
    const currentButton = target.getAttribute('direction');
    this[currentButton].checked = !(this.direction === currentButton);
    this.onChange();
  }
}
